import React from 'react';

import {
  DatePicker,
  DatePickerProps,
} from '@ast/magma/components/datePicker';
import {
  DatePickerOnChangeParam,
} from '@ast/magma/components/datePicker/calendar/calendar';

import { mitigateTimezone } from '@app/common/utils/dateTime';

import styles from './DateField.pcss';

export type DateFieldValue = Date | '' | null;

export type DateFieldProps = Omit<DatePickerProps, 'onChange' | 'isDateDisabled'> & {
  /**
   * Magma's datepicker expects only boolean as result,
   * but we need string if date is invalid for
   * react-hook-form's validation mechanism to display
   * proper validation message.
   */
  readonly isDateDisabled?: (date: Date) => string | boolean
  /**
   * Magma's datepicker fires Date | null as onChange' value.
   * Datepicker has null value when input is:
   * empty, invalid format, invalid date (isDateDisabled returns false).
   * We need to distinguish case for different validation messages: wrong format | empty field.
   * Datepicker onChange' callback fires '' (empty string) for empty field
   * and null value for wrong format.
   */
  readonly onChange?: (value: DateFieldValue) => void
};

export type DateFieldRef = HTMLInputElement;

export const DateField = React.forwardRef((
  props: DateFieldProps,
  ref: React.Ref<DateFieldRef>,
) => {
  const {
    isDateDisabled,
    onChange,
    ...otherProps
  } = props;

  const copyRef = React.useRef<DateFieldRef | null>(null);

  return (
    <DatePicker
      className={styles.field}
      data-stable-name="DatePickerInput"
      {...otherProps}
      isDateDisabled={(
        isDateDisabled
          ? (date: Date) => Boolean(isDateDisabled(date))
          : undefined
      )}
      onChange={(selectedDate: DatePickerOnChangeParam | null) => {
        if (!onChange) return;

        let date: DateFieldValue = selectedDate?.date || null;

        if (date) {
          /**
           * Example:
           * const date = new Date(2022, 7, 30)
           * -> Tue Aug 30 2022 00:00:00 GMT+0300
           * using date.toISOString()
           * -> 2022-08-29T21:00:00.000Z

           * To prevent date such deviations,
           * we should include timezone offset.
           */
          date = mitigateTimezone(date);
        } else if (!date && copyRef.current?.value === '') {
          date = '';
        }

        onChange(date);
      }}
      ref={(elm: HTMLInputElement) => {
        if (typeof ref === 'function') ref(elm);
        copyRef.current = elm;
      }}
    />
  );
});
